"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type AppLocale } from "@/i18n/routing";

type Props = {
  variant?: "select" | "icon";
};

export function LocaleSwitcher({ variant = "select" }: Props) {
  const t = useTranslations("nav");
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  function onChange(next: string) {
    router.replace(pathname, { locale: next as AppLocale });
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (variant === "icon") {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          aria-label={t("language")}
          title={t("language")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        >
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9s-1.3 6.2-3.8 9c-2.5-2.8-3.8-5.8-3.8-9S9.5 5.8 12 3z" />
          </svg>
        </button>
        {open ? (
          <div className="absolute right-0 z-50 mt-1 min-w-[7rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {routing.locales.map((code) => (
              <button
                key={code}
                type="button"
                className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  code === locale ? "font-semibold text-[var(--brand-green)]" : ""
                }`}
                onClick={() => onChange(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <label className="inline-flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
      <span className="sr-only">{t("language")}</span>
      <select
        className="rounded-md border border-stone-400 bg-white px-2 py-1 text-stone-900 dark:border-stone-600 dark:bg-stone-950 dark:text-stone-100"
        value={locale}
        onChange={(event) => onChange(event.target.value)}
        aria-label={t("language")}
      >
        {routing.locales.map((code) => (
          <option key={code} value={code}>
            {code.toUpperCase()}
          </option>
        ))}
      </select>
    </label>
  );
}
